import {FC} from 'react';
import type {AxiosError} from 'axios';
import {useValidatePassword, type ValidatePasswordResponse} from '@rocket-ui/lk-api';
import type {AuthResponseSource} from 'features/auth/model/postAuthRedirect';
import {PasswordInput} from './PasswordInput';
import {PasswordNumericKeypad} from './PasswordNumericKeypad';
import {useUserInteraction} from '@rocket-ui/user-interaction';
import {useAutoSubmitCodeLikeInput} from '../useAutoSubmitCodeLikeInput';
import {useIsMaxSm} from '../useIsMaxSm';
import styles from './PasswordInputForm.module.css';

interface PasswordInputFormProps {
	token: string;
	onForgotPasswordClick?: () => void;
	isForgotPasswordLoading?: boolean;
	onSubmit: (response: ValidatePasswordResponse, source: AuthResponseSource) => void;
}

const SHAKE_DURATION_MS = 820;
const PIN_LENGTH = 4;

export const PasswordInputForm: FC<PasswordInputFormProps> = ({
	token,
	onForgotPasswordClick,
	isForgotPasswordLoading,
	onSubmit,
}) => {
	const {notifyUser} = useUserInteraction();
	const {mutateAsync: validatePassword, isLoading} = useValidatePassword();
	const isMaxSm = useIsMaxSm();

	const {
		value: code,
		onChange: handleChange,
		errorNonce,
		inputRef,
	} = useAutoSubmitCodeLikeInput({
		requiredLength: PIN_LENGTH,
		clearAfterErrorMs: SHAKE_DURATION_MS,
		notifyUser,
		errorMessageFallback: 'Ошибка при вводе пароля',
		getErrorMessage: (err) => (err as AxiosError<{errors?: string[]}> | undefined)?.response?.data?.errors?.[0],
		submit: async (value) => {
			const data = await validatePassword({token, password: value});
			onSubmit(data, 'validate');
		},
	});

	return (
		<section className={styles.section}>
			<p className={styles.title}>Введите пароль</p>

			<div className={styles.inputWrap}>
				<PasswordInput
					value={code}
					onChange={handleChange}
					isLoading={isLoading}
					disabled={isLoading}
					ref={inputRef as React.RefObject<HTMLInputElement>}
					errorNonce={errorNonce}
					suppressNativeKeyboard={isMaxSm}
				/>
			</div>

			<div className={styles.meta}>
				<button
					className={styles.forgotLink}
					type="button"
					onClick={onForgotPasswordClick}
					disabled={isForgotPasswordLoading}
				>
					Забыл пароль
				</button>
			</div>

			<PasswordNumericKeypad
				value={code}
				maxLength={PIN_LENGTH}
				disabled={isLoading}
				onDigit={(digit) => handleChange((code + digit).slice(0, PIN_LENGTH))}
				onBackspace={() => handleChange(code.slice(0, -1))}
			/>
		</section>
	);
};
